/**
 * Free gift the shopper takes with one tap in the cart.
 *
 * The money side is not ours: an automatic discount in the admin zeroes the gift
 * when a qualifying product is present. This only saves the shopper from having to
 * add it by hand -- and takes it back out when the qualifying product leaves,
 * because a gift left behind stops being discounted and quietly turns into a 99
 * shekel line nobody chose.
 *
 * WHY NOTHING IS ADDED AUTOMATICALLY ANY MORE. 2026-08-26. The gift used to put
 * itself in at the moment a qualifying product was added, and the add was held back
 * until it landed so the drawer would open already carrying it. That hold cost about
 * a second on exactly the four products the shop lives on, and the owner reported
 * conversions falling after it shipped. Merging the gift into the same request fixed
 * the second and silently cost the storefront_customer_tracking event Shopify builds
 * its "added to cart" report from -- measured, 0 of 5 runs against 5 of 5; see
 * docs/theme-patches.md. So the gift is offered instead of imposed: adding a product
 * is now untouched by this file, and snippets/cart-gift-offer.liquid draws a one-tap
 * offer in the drawer and on /cart for as long as the cart earns a gift and has none.
 *
 * That also keeps a removal final without storing the refusal anywhere -- no cart
 * attribute (those surface in the merchant's order), no localStorage (Liquid cannot
 * read it, so the drawer could not be drawn from it). Nothing ever adds the gift
 * except the shopper's own tap.
 *
 * The gift line carries a hidden `_gift` property. That is what tells it apart from
 * the same product bought deliberately: without it we would remove an item the
 * shopper paid for. Properties starting with an underscore are hidden at checkout,
 * so the line reads as an ordinary product at 0.
 *
 * How the gift LOOKS is not this file's business: Liquid reads the same flag, so
 * snippets/cart-gift-line.liquid and snippets/cart-gift-offer.liquid draw both
 * states. What is here is the cart itself and the two buttons that change it.
 *
 * Cart mutations are still caught by wrapping fetch, but ONLY the ones that can
 * leave a gift stranded: change, update, clear. An add passes through untouched and
 * unwaited -- that is the whole point of this rewrite, and it is why the button on a
 * product with a gift is now exactly as fast as on any other product. Our own
 * requests use the saved original, so they never re-enter. After a change lands the
 * theme is asked to redraw itself through its own event bus -- we render nothing.
 *
 * Config comes from snippets/gift-auto-add.liquid (theme settings).
 */
(() => {
  const config = window.themeGiftConfig;
  if (!config?.enabled || !config.variantId || !config.qualifyingIds?.length) return;

  const GIFT_FLAG = '_gift';

  const qualifying = new Set(config.qualifyingIds);
  const root = window.Shopify?.routes?.root ?? '/';
  const nativeFetch = window.fetch.bind(window);

  const isCartMutation = (url) => /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url);
  const isCartAdd = (url) => /\/cart\/add(\.js)?(\?|$)/.test(url);

  const isGiftProduct = (item) => (item.variant_id ?? item.id) === config.variantId;

  const postJson = (path, body) =>
    nativeFetch(root + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });

  const readCart = async () => {
    const response = await nativeFetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`cart.js responded ${response.status}`);
    return response.json();
  };

  const addGift = () =>
    postJson('cart/add.js', {
      items: [{ id: config.variantId, quantity: 1, properties: { [GIFT_FLAG]: 'true' } }]
    });

  /** Ask the theme to redraw. On the cart page there is no drawer to redraw and the
   *  markup was rendered before our change, so the page has to come back fresh. */
  const refresh = async () => {
    if (/\/cart\/?$/.test(window.location.pathname)) {
      window.location.reload();
      return;
    }

    window.eventBus?.emit('render:cart:drawer');

    try {
      window.wetheme?.updateCartCount?.(await readCart());
    } catch {
      /* The count is cosmetic; a failure here must not surface to the shopper. */
    }
  };

  /** Work is queued, not dropped. A boolean guard used to skip whatever arrived
   *  while a pass was running, and a skipped pass is never retried: remove the
   *  qualifying product during one, and the gift stayed in the cart at full price
   *  until the next page load. */
  let queue = Promise.resolve();

  const enqueue = (task) => {
    const run = queue.then(task, task);
    queue = run.catch(() => {});
    return run;
  };

  /**
   * Takes the gift away or trims it, and never puts one in. The only thing that adds
   * is the shopper's tap on the offer.
   */
  const reconcile = () =>
    enqueue(async () => {
      try {
        const cart = await readCart();
        const gift = cart.items.find((item) => item.properties?.[GIFT_FLAG]);
        const qualifies = cart.items.some(
          (item) => !item.properties?.[GIFT_FLAG] && qualifying.has(item.product_id)
        );

        let changed = false;

        if (gift && !qualifies) {
          await postJson('cart/change.js', { id: gift.key, quantity: 0 });
          changed = true;
        } else if (gift && gift.quantity > 1) {
          // The discount frees exactly one. A second copy would be charged in full.
          await postJson('cart/change.js', { id: gift.key, quantity: 1 });
          changed = true;
        }

        if (changed) await refresh();
      } catch (error) {
        console.error('Gift auto-add:', error);
      }
    });

  /** Reconciling on load is not belt and braces, it is the only cover for the ways
   *  a cart changes without a fetch. The cart page removes a line through a plain
   *  link (`/cart/change?line=...`), the browser navigates, and no wrapper of ours
   *  ever runs -- the gift stayed behind, no longer discounted, priced at full. The
   *  same goes for a shopper returning days later to a cart left in the cookie.
   *  It cannot add: arriving on a page is not asking for anything. */
  const start = () => {
    reconcile();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  /* The two buttons the shopper has over the gift, both drawn by Liquid. Delegated
     from the document because the drawer replaces its own DOM on every render, so a
     listener bound at load time would sit on a node that no longer exists. */
  document.addEventListener('click', (event) => {
    const remover = event.target.closest?.('[data-gift-remove]');
    if (remover) {
      event.preventDefault();
      remover.disabled = true;
      enqueue(async () => {
        try {
          await postJson('cart/change.js', { id: remover.dataset.giftRemove, quantity: 0 });
          await refresh();
        } catch (error) {
          console.error('Gift auto-add:', error);
          remover.disabled = false;
        }
      });
      return;
    }

    const taker = event.target.closest?.('[data-gift-take]');
    if (!taker) return;

    event.preventDefault();
    taker.disabled = true;
    enqueue(async () => {
      try {
        const added = await addGift();
        if (added.ok) await refresh();
        else taker.disabled = false;
      } catch (error) {
        console.error('Gift auto-add:', error);
        taker.disabled = false;
      }
    });
  });

  window.fetch = async (...args) => {
    const response = await nativeFetch(...args);

    const target = args[0];
    const url = String(typeof target === 'string' ? target : (target?.url ?? ''));

    // An add is none of our business now: nothing is held, nothing is inspected, and
    // the shopper's own product reaches the drawer at the speed the theme intended.
    // A gift cannot be stranded by an add either -- an add can only make a cart
    // qualify, never stop it qualifying.
    if (!response.ok || !isCartMutation(url) || isCartAdd(url)) return response;

    // Everything else can leave a gift behind in a cart that no longer earns it, and
    // a gift left behind stops being discounted: 99 shekels nobody chose. Not waited
    // on -- the theme has nothing to redraw that depends on the answer.
    reconcile();
    return response;
  };
})();
